import type { DeliveryCity } from '@/entities/order/types/delivery'

import { apiSlice } from '@/shared/api/apiSlice'
import { ServerResponse } from '@/shared/types/common'

const objectToFormData = (obj: Record<string, string>): FormData => {
	const formData = new FormData()
	Object.entries(obj).forEach(([key, value]) => {
		formData.append(key, value ?? '')
	})
	return formData
}

export type FLPDeliveryShop = {
	UF_TITLE: string
	UF_B24_VALUE_ID: string
}

export type FLPDeliveryItem = {
	ID: number
	NAME: string
	TYPE: 'shipping' | 'pvz' | 'shop'
	SHOPS?: FLPDeliveryShop[]
}

export type FLPPaymentItem = {
	ID: string
	NAME: string
}

type MutationMeta = {
	response?: {
		status?: number
	}
}

const getStatusCodeFromMeta = (meta: unknown): number => {
	if (typeof meta !== 'object' || meta === null || !('response' in meta)) {
		return 0
	}

	const response = (meta as MutationMeta).response
	return typeof response?.status === 'number' ? response.status : 0
}

export const flpApi = apiSlice.injectEndpoints({
	overrideExisting: false,
	endpoints: builder => ({
		getCities: builder.query<DeliveryCity[], string>({
			query: cityName => ({
				url: 'delivery/cities',
				params: { CITY_NAME: cityName }
			}),
			transformResponse: (res: ServerResponse<DeliveryCity[]>): DeliveryCity[] => {
				if (!res?.data?.length) return []
				return res.data
			}
		}),
		getDeliveryOptions: builder.query<FLPDeliveryItem[], string>({
			query: cityName => ({
				url: 'form/best-price/delivery',
				params: { CITY: cityName }
			}),
			transformResponse: (res: ServerResponse<FLPDeliveryItem[]>): FLPDeliveryItem[] => {
				if (!res?.data?.length) return []
				return res.data
			}
		}),
		getPaymentOptions: builder.query<FLPPaymentItem[], { city: string; delivery: string }>({
			query: ({ city, delivery }) => ({
				url: 'form/best-price/payment',
				params: { CITY: city, DELIVERY: delivery }
			}),
			transformResponse: (res: ServerResponse<FLPPaymentItem[]>): FLPPaymentItem[] => {
				if (!res?.data?.length) return []
				return res.data
			}
		}),
		sendRequest: builder.mutation<{ code: number }, Record<string, string>>({
			query: data => ({
				url: 'b24-element-add',
				body: objectToFormData(data),
				method: 'POST'
			}),
			transformResponse: (_result, meta) => ({
				code: getStatusCodeFromMeta(meta)
			})
		})
	})
})

export const {
	useGetCitiesQuery,
	useLazyGetDeliveryOptionsQuery,
	useLazyGetPaymentOptionsQuery,
	useSendRequestMutation
} = flpApi
